import { I18NextService } from 'angular-i18next';
import { ITagDTO, ITagUsageDTO } from '@modeso/types__aumico-lib-companies-be';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { LANGUAGES } from '@modeso/aumico-lib-core-fe';
import {
    FX_MINIMUM_TERM_MONTHS,
    FX_PRICE_CHF_PER_YEAR,
    FileMetaData,
    ICompanyDTO,
    IFxSettingsDTO,
} from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { ILegalFormDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs';
import { HamSearchDropDownDirective } from '../../../../../aumico-components/src/lib';
import { GetUserDto } from '../../../../../aumico-lib-users-fe/src/public-api';
import { localizationKeys } from '../../constants/localizationKeys';
import { ZEFIX_COUNTRY_CODE, ZEFIX_UID_PATTERN, formatZefixUid, stripZefixUid } from '../../constants/zefix-legal-form-mapping';
import { IZefixCompanyDTO, IZefixDiffField } from '../../dtos/zefix-company.dto';
import { TasksUpdate } from '../../enums/tasks-update.enum';
import { ZefixService } from '../../services/zefix.service';

/** Duration of the FX activation animation; must match the SCSS keyframes. */
const FX_CELEBRATION_MS = 2200;

@UntilDestroy()
@Component({
    selector: 'aumico-company-data-form',
    templateUrl: 'company-data-form.component.html',
    styleUrls: ['company-data-form.component.scss'],
})
export class CompanyDataFormComponent implements OnInit, OnChanges, OnDestroy {
    private latestSelectedLegalForm: INameId | undefined;

    local = localizationKeys;
    showWarningLegalFormNotExist: boolean;
    @ViewChild(HamSearchDropDownDirective) searchUserDirective!: HamSearchDropDownDirective;

    @Input() active: boolean;
    @Input() hideStatus: boolean = false;
    @Input() companyFormGroup: UntypedFormGroup;
    @Input() countryOptions: INameId[];
    @Input() legalFormsOptions: INameId[] = [];
    @Input() originalLegalForms: ILegalFormDTO[] = [];
    @Input() currentLanguage: String = LANGUAGES.SWISS_GERMAN;
    @Input() defaultLanguage: String = LANGUAGES.SWISS_GERMAN;
    @Input() industryOptions: INameId[] = [];
    @Input() organizationUsers: INameId[] = [];
    @Input() logo: File;
    @Input() fileMetadata: FileMetaData;
    @Input() assignedUser: GetUserDto;
    @Input() clerkUser: GetUserDto;
    @Input() workflowOptions: INameId[] = [];
    @Input() organizationHasCpmmActive: boolean = false;
    @Input() editMode = false;
    @Input() isDeleting: boolean = false;
    @Input() activeCompany: ICompanyDTO | null = null;

    /** Functional Currency (REV-21512) — true only for org admins. */
    @Input() canManageFx = false;
    /** True while an FX activate/deactivate request is in flight. */
    @Input() fxBusy = false;

    private _organizationHasMandateAndTaskManagementEnabled: boolean = false;
    @Input() set organizationHasMandateAndTaskManagementEnabled(value: boolean) {
        this._organizationHasMandateAndTaskManagementEnabled = value;
        this.previousWorkflow = this.companyFormGroup.get('workflow').value;
    }
    get organizationHasMandateAndTaskManagementEnabled(): boolean {
        return this._organizationHasMandateAndTaskManagementEnabled;
    }

    /** Functional Currency (REV-21512) — the page performs the actual calls. */
    // REV-22155: tag box (both forms) and "Verknüpfte Personen" (profile only).
    @Input() tags: ITagDTO[] = [];
    @Input() tagUsage: { id: string; usage: ITagUsageDTO } | null = null;
    /** Passed straight to the tag box so it stops waiting after a failed tag call. */
    @Input() tagError: unknown = null;
    @Input() linkedPersons: { _id: string; name: string; role: string }[] = [];
    @Output() tagCreate = new EventEmitter<string>();
    @Output() tagDeleteRequest = new EventEmitter<string>();
    @Output() tagDeleteConfirm = new EventEmitter<string>();
    @Output() tagDeleteCancel = new EventEmitter<void>();
    @Output() linkedPersonClick = new EventEmitter<string>();

    @Output() fxActivate = new EventEmitter<void>();
    @Output() fxDeactivate = new EventEmitter<void>();
    /** Consent given while creating: the page activates once the company has an id. */
    @Output() fxPendingActivationChange = new EventEmitter<boolean>();

    @Output() countrySelectionChange = new EventEmitter<string>();
    @Output() fileUploaded = new EventEmitter<File>();
    private currentSelectedAssignee: string;
    private currentSelectedClerk: string;
    assignedOn: Date;
    assignedOnClerk: Date | null;
    currentSelectedAssigneeName: string;
    currentSelectedClerkName: string;
    selectedTaskUpdate = TasksUpdate.UPDATE_TASKS_ASSIGNED_TO_CURRENT;

    uploadedLogo: string | ArrayBuffer;

    defaultUploadImage = 'assets/upload.png';
    showUploadDialog: boolean;

    fileSizeError: boolean;

    openClerkChangeConfirmationDialog = false;
    @Output() tasksUpdateSelection: EventEmitter<TasksUpdate | undefined> = new EventEmitter();
    tasksUpdate = TasksUpdate;

    private originalLogo: File | null = null;

    openWorkFlowChangeConfirmationDialog = false;
    previousWorkflow: INameId | undefined;
    pendingWorkflow: INameId | undefined;

    // ── Zefix state ──────────────────────────────────────────────────────────
    zefixImportedData: IZefixCompanyDTO | null = null;
    zefixFilledFields: Set<string> = new Set();

    showDiffModal = false;
    diffLoading = false;
    diffFields: IZefixDiffField[] = [];
    diffHasNoChanges = false;

    // ── Functional Currency (FX) ─────────────────────────────────────────────
    readonly fxPrice = FX_PRICE_CHF_PER_YEAR;
    readonly fxTermMonths = FX_MINIMUM_TERM_MONTHS;

    showFxConsentModal = false;
    showFxDeactivateModal = false;
    fxConsentAccepted = false;

    /** Create mode: the terms were accepted, but there is no company to activate yet. */
    fxPendingActivation = false;

    /** Drives the one-shot activation animation. */
    fxJustActivated = false;
    /** `null` until the first company arrives, so loading an already-active company does not celebrate. */
    private previousFxEnabled: boolean | null = null;
    private fxCelebrationTimer: ReturnType<typeof setTimeout> | undefined;

    get fxSettings(): IFxSettingsDTO | null {
        return this.activeCompany?.fx ?? null;
    }

    get isFxActive(): boolean {
        return this.fxSettings?.enabled === true;
    }

    /** Shown between accepting the terms and the company existing — create mode only. */
    get isFxPending(): boolean {
        return this.fxPendingActivation && !this.isFxActive;
    }

    openFxConsentModal(): void {
        this.fxConsentAccepted = false;
        this.showFxConsentModal = true;
    }

    cancelFxConsent(): void {
        this.showFxConsentModal = false;
        this.fxConsentAccepted = false;
    }

    confirmFxConsent(): void {
        if (!this.fxConsentAccepted) {
            return;
        }
        this.showFxConsentModal = false;
        // Activation is bound to a company id, which a company being created does not have yet —
        // remember the consent and let the page activate once the company was written.
        if (!this.editMode) {
            this.setFxPendingActivation(true);

            return;
        }
        this.fxActivate.emit();
    }

    cancelFxPendingActivation(): void {
        this.setFxPendingActivation(false);
    }

    private setFxPendingActivation(pending: boolean): void {
        this.fxPendingActivation = pending;
        this.fxPendingActivationChange.emit(pending);
    }

    openFxDeactivateModal(): void {
        this.showFxDeactivateModal = true;
    }

    confirmFxDeactivate(): void {
        this.showFxDeactivateModal = false;
        this.fxDeactivate.emit();
    }

    ngOnDestroy(): void {
        clearTimeout(this.fxCelebrationTimer);
    }

    /** Celebrate only the off→on transition, never an already-active company being loaded. */
    private trackFxActivation(): void {
        const isEnabled = this.isFxActive;
        if (this.previousFxEnabled === false && isEnabled) {
            this.fxJustActivated = true;
            clearTimeout(this.fxCelebrationTimer);
            this.fxCelebrationTimer = setTimeout(() => (this.fxJustActivated = false), FX_CELEBRATION_MS);
        }
        this.previousFxEnabled = isEnabled;
    }

    // ── Zefix computed ───────────────────────────────────────────────────────
    get isZefixActive(): boolean {
        const countryValue = this.companyFormGroup?.get('country')?.value;
        const selectedId = countryValue?.id ?? countryValue;
        return selectedId === ZEFIX_COUNTRY_CODE;
    }

    get showZefixSearch(): boolean {
        return this.isZefixActive && !this.zefixImportedData;
    }

    get showImportBanner(): boolean {
        return !!this.zefixImportedData;
    }

    get existingUidIsZefix(): boolean {
        const uid = this.companyFormGroup?.get('UID')?.value;
        return uid ? ZEFIX_UID_PATTERN.test(uid) : false;
    }

    get zefixLastSyncedAt(): Date | null {
        const val = this.companyFormGroup?.get('zefix_last_synced_at')?.value;
        return val ? new Date(val) : null;
    }

    constructor(private zefixService: ZefixService, private readonly i18next: I18NextService) { }

    triggerSearch() {
        this.searchUserDirective.startSearch();
    }

    onFileSizeError(error: boolean) {
        this.fileSizeError = error;
    }

    notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value.toString().trim().length() === 0) {
            return { required: true };
        }
        return null;
    }

    ngOnInit(): void {
        this.currentSelectedAssignee = this.companyFormGroup.get('assignee').value?.id as string;
        this.currentSelectedClerk = this.companyFormGroup.get('clerk').value?.id as string;
        this.assignedOn = this.companyFormGroup.get('assignedOn').value as Date;

        const assignedOnClerkValue = this.companyFormGroup.get('assignedOnClerk')?.value;
        this.assignedOnClerk = assignedOnClerkValue ? new Date(assignedOnClerkValue) : null;

        this.previousWorkflow = this.companyFormGroup.get('workflow').value;

        const assigneeValue = this.companyFormGroup.get('assignee').value;
        if (assigneeValue) {
            this.currentSelectedAssigneeName = assigneeValue.name;
        }

        const clerkValue = this.companyFormGroup.get('clerk').value;
        if (clerkValue) {
            this.currentSelectedClerkName = clerkValue.name;
        }

        if (this.logo) {
            this.originalLogo = this.logo;
        }

        this.companyFormGroup
            .get('legalform')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe(() => {
                if (this.isDeleting) return;
                this.showWarningLegalFormNotExist = false;
                const selectedLegalFormValue = this.companyFormGroup.get('legalform').value;
                if (selectedLegalFormValue != null) {
                    this.latestSelectedLegalForm = selectedLegalFormValue;
                }
            });

        this.companyFormGroup
            .get('assignee')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((value) => {
                if (this.isDeleting) return;
                if (value == null) return;
                this.currentSelectedAssigneeName = value.name;
                if (value.id === this.currentSelectedAssignee) {
                    this.companyFormGroup.get('assignedOn').setValue(this.assignedOn);
                } else {
                    this.companyFormGroup.get('assignedOn').setValue(value ? new Date() : null);
                }
                this.currentSelectedAssignee = value.id;
            });

        this.companyFormGroup
            .get('clerk')
            .valueChanges.pipe(untilDestroyed(this))
            .subscribe((value) => {
                if (this.isDeleting) return;
                if (value == null) return;
                if (this.organizationHasCpmmActive && this.editMode) {
                    this.currentSelectedClerkName = value.name;
                    this.openClerkChangeConfirmationDialog = true;
                } else {
                    this.currentSelectedClerkName = value.name;
                    if (value.id === this.currentSelectedClerk) {
                        this.companyFormGroup.get('assignedOnClerk').setValue(this.assignedOnClerk);
                    } else {
                        this.companyFormGroup.get('assignedOnClerk').setValue(value ? new Date() : null);
                    }
                    this.currentSelectedClerk = value.id;
                    this.tasksUpdateSelection.emit(TasksUpdate.UPDATE_ALL_TASKS);
                }
            });

        this.companyFormGroup
            .get('workflow')
            .valueChanges.pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe((value) => {
                if (this.isDeleting) return;
                if (this.organizationHasCpmmActive && this.editMode) {
                    this.openWorkFlowChangeConfirmationDialog = true;
                    this.companyFormGroup.get('workflow').setValue(this.previousWorkflow, { emitEvent: false });
                    this.pendingWorkflow = value;
                } else {
                    this.previousWorkflow = value;
                }
            });
    }

    confirmWorkflowChange() {
        this.openWorkFlowChangeConfirmationDialog = false;
        if (this.pendingWorkflow) {
            this.companyFormGroup.get('workflow').setValue(this.pendingWorkflow);
            this.previousWorkflow = this.pendingWorkflow;
            this.pendingWorkflow = null;
        }
    }

    cancelWorkflowChange() {
        this.openWorkFlowChangeConfirmationDialog = false;
        this.pendingWorkflow = null;
    }

    closeClerkChangeConfirmationDialog() {
        this.openClerkChangeConfirmationDialog = false;
        this.selectedTaskUpdate = undefined;
    }

    updateClerk() {
        this.currentSelectedClerkName = this.companyFormGroup.get('clerk').value.name;
        this.tasksUpdateSelection.emit(this.selectedTaskUpdate);
        if (this.companyFormGroup.get('clerk').value.id === this.currentSelectedClerk) {
            this.companyFormGroup.get('assignedOnClerk').setValue(this.assignedOnClerk);
        } else {
            this.companyFormGroup
                .get('assignedOnClerk')
                .setValue(this.companyFormGroup.get('clerk').value ? new Date() : null);
        }
        this.currentSelectedClerk = this.companyFormGroup.get('clerk').value.id;
        this.closeClerkChangeConfirmationDialog();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes != null && changes['legalFormsOptions'] != null) {
            this.legalFormChangeHandler();
        }
        if (changes != null && changes['logo'] != null) {
            this.originalLogo = this.logo;
            if (this.logo != null) {
                this.readFile(this.logo);
            }
        }
        if (changes != null && changes['assignedUser'] != null) {
            this.currentSelectedAssigneeName = this.assignedUser?.firstName + ' ' + this.assignedUser?.lastName;
        }
        if (changes != null && changes['clerkUser'] != null) {
            this.currentSelectedClerkName = this.clerkUser?.firstName + ' ' + this.clerkUser?.lastName;
        }
        if (changes != null && changes['activeCompany'] != null) {
            this.trackFxActivation();
            // Defer one tick so patchValue in the parent has settled before we read form values
            setTimeout(() => this.restoreZefixStateFromActiveCompany(), 0);
        }
    }

    private restoreZefixStateFromActiveCompany(): void {
        if (!this.activeCompany?.zefix_linked || !this.activeCompany?.zefix_uid) {
            return;
        }
        // Only restore if not already set (don't overwrite a fresh search-selection)
        if (this.zefixImportedData != null) {
            return;
        }
        this.zefixImportedData = {
            uid: this.activeCompany.zefix_uid,
            name: this.activeCompany.name ?? '',
            legalFormId: null,
            legalFormText: null,
            status: null,
            address: {
                street: this.activeCompany.street ?? null,
                zip: this.activeCompany.zip ?? null,
                city: this.activeCompany.city ?? null,
                canton: null,
            },
            registryOfCommerceId: null,
        } as unknown as IZefixCompanyDTO;

        // Mark all fields that have a value as Zefix-filled so the badges show
        // — except fields the user has manually overridden (those stay yellow).
        // Read values from activeCompany directly (not form controls) so that
        // legalFormChangeHandler() nullifying the control doesn't break state restoration.
        const overriddenFields: string[] = this.activeCompany.zefix_overridden_fields ?? [];
        const zefixFields = ['name', 'street', 'zip', 'city', 'UID', 'legalform'];
        zefixFields.forEach((field) => {
            const companyValue = (this.activeCompany as unknown as Record<string, unknown>)[field];
            if (companyValue && !overriddenFields.includes(field)) {
                this.zefixFilledFields.add(field);
            }
        });
    }

    // ── Zefix handlers ────────────────────────────────────────────────────────

    onZefixCompanySelected(co: IZefixCompanyDTO): void {
        this.zefixService.getCompanyByUid(co.uid)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (full) => this.patchFormFromZefix(full),
                error: () => this.patchFormFromZefix(co),
            });
    }

    private patchFormFromZefix(co: IZefixCompanyDTO): void {
        this.zefixImportedData = co;
        this.zefixFilledFields = new Set();

        const street = [co.address?.street, co.address?.houseNumber].filter(Boolean).join(' ');
        // Prefer BE-resolved key; fall back to text for legacy data
        const matchedLegalForm = this.findMatchingLegalForm(co.legalFormAumicoKey, co.legalFormText);

        const patches: Record<string, unknown> = {
            name: co.name,
            street,
            zip: co.address?.zip,
            city: co.address?.city,
            UID: formatZefixUid(co.uid),
        };

        if (matchedLegalForm) {
            patches['legalform'] = matchedLegalForm;
        }

        Object.entries(patches).forEach(([key, value]) => {
            if (value) {
                this.companyFormGroup.get(key)?.setValue(value);
                this.zefixFilledFields.add(key);
            }
        });

        this.companyFormGroup.get('zefix_uid')?.setValue(stripZefixUid(co.uid) ?? null);
        this.companyFormGroup.get('zefix_linked')?.setValue(true);
        this.companyFormGroup.get('zefix_last_synced_at')?.setValue(new Date());
        this.companyFormGroup.get('zefix_overridden_fields')?.setValue([]);
    }

    onZefixManualEntry(): void {
        // User chose to enter manually — Zefix search panel stays visible, nothing to do
    }

    onNewSearch(): void {
        this.zefixImportedData = null;
        this.zefixFilledFields = new Set();
    }

    isZefixField(controlName: string): boolean {
        return this.zefixFilledFields.has(controlName);
    }

    isZefixOverridden(controlName: string): boolean {
        const overriddenFields: string[] = this.companyFormGroup?.get('zefix_overridden_fields')?.value ?? [];
        return overriddenFields.includes(controlName);
    }

    onZefixFieldModified(controlName: string): void {
        this.zefixFilledFields.delete(controlName);

        // Persist the override so it survives a page reload
        const overrideControl = this.companyFormGroup.get('zefix_overridden_fields');
        if (overrideControl) {
            const current: string[] = overrideControl.value ?? [];
            if (!current.includes(controlName)) {
                overrideControl.setValue([...current, controlName]);
            }
        }
    }

    openRefreshModal(): void {
        const uidForRefresh = this.zefixImportedData?.uid ?? this.companyFormGroup.get('zefix_uid')?.value;
        if (!uidForRefresh) return;
        this.showDiffModal = true;
        this.diffLoading = true;
        this.diffFields = [];
        this.diffHasNoChanges = false;

        this.zefixService.refresh(uidForRefresh)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (fresh) => {
                    this.diffFields = this.buildDiffFields(fresh);
                    this.diffHasNoChanges = this.diffFields.length === 0;
                    this.diffLoading = false;
                },
                error: () => {
                    this.diffLoading = false;
                    this.showDiffModal = false;
                },
            });
    }

    applyDiff(): void {
        if (!this.diffFields.length) {
            this.showDiffModal = false;
            return;
        }

        const appliedKeys = this.diffFields.map((diff) => diff.fieldKey);

        this.diffFields.forEach((diff) => {
            this.companyFormGroup.get(diff.fieldKey)?.setValue(diff.resolvedValue ?? diff.newValue);
            this.zefixFilledFields.add(diff.fieldKey);
        });

        // Remove re-synced fields from the overridden list
        const overrideControl = this.companyFormGroup.get('zefix_overridden_fields');
        if (overrideControl) {
            const current: string[] = overrideControl.value ?? [];
            overrideControl.setValue(current.filter((f) => !appliedKeys.includes(f)));
        }

        this.companyFormGroup.get('zefix_last_synced_at')?.setValue(new Date());

        this.showDiffModal = false;
    }

    closeDiffModal(): void {
        this.showDiffModal = false;
    }

    private buildDiffFields(fresh: IZefixCompanyDTO): IZefixDiffField[] {
        const street = [fresh.address?.street, fresh.address?.houseNumber].filter(Boolean).join(' ');
        const candidates: Array<{ fieldKey: string; labelKey: string; newValue: string }> = [
            { fieldKey: 'name', labelKey: localizationKeys.aumico_companies_form_label_name, newValue: fresh.name },
            { fieldKey: 'street', labelKey: localizationKeys.aumico_companies_form_label_street_and_number, newValue: street },
            { fieldKey: 'zip', labelKey: localizationKeys.aumico_companies_form_label_zip_code, newValue: fresh.address?.zip },
            { fieldKey: 'city', labelKey: localizationKeys.aumico_companies_form_label_city, newValue: fresh.address?.city },
            { fieldKey: 'UID', labelKey: localizationKeys.aumico_companies_form_label_uid_number, newValue: formatZefixUid(fresh.uid) },
        ];

        const result: IZefixDiffField[] = candidates
            .map((c) => ({
                ...c,
                currentValue: this.companyFormGroup.get(c.fieldKey)?.value ?? '',
            }))
            .filter((c) => c.currentValue !== c.newValue && c.newValue);

        // ── Legalform diff ───────────────────────────────────────────────────
        const matchedLegalForm = this.findMatchingLegalForm(fresh.legalFormAumicoKey, fresh.legalFormText);
        const currentLegalForm = this.companyFormGroup.get('legalform')?.value;
        if (matchedLegalForm && matchedLegalForm.id !== currentLegalForm?.id) {
            result.push({
                fieldKey: 'legalform',
                labelKey: localizationKeys.aumico_zefix_field_legalform,
                currentValue: currentLegalForm?.name ?? '—',
                newValue: matchedLegalForm.name ?? matchedLegalForm.id,
                resolvedValue: matchedLegalForm,
            });
        }

        return result;
    }

    private findMatchingLegalForm(aumicoKey: string | null | undefined, fallbackText?: string | null): INameId | null {
        if (!this.legalFormsOptions?.length && !this.originalLegalForms?.length) return null;

        if (aumicoKey && this.originalLegalForms?.length) {
            const matchedByKey = this.originalLegalForms.find((legalForm) => legalForm.name === aumicoKey);
            if (matchedByKey) {
                return this.mapLegalFormToOption(matchedByKey);
            }
        }

        if (!fallbackText) return null;

        const normalizedFallbackText = fallbackText.toLowerCase();
        const matchedByText = this.originalLegalForms?.find(
            (legalForm) =>
                this.getLocalizedLegalFormName(legalForm).toLowerCase() === normalizedFallbackText ||
                legalForm.text?.['de-ch']?.toLowerCase() === normalizedFallbackText ||
                legalForm.name?.toLowerCase() === normalizedFallbackText,
        );

        if (matchedByText) {
            return this.mapLegalFormToOption(matchedByText);
        }

        return this.legalFormsOptions.find((legalForm) => legalForm.name?.toLowerCase() === normalizedFallbackText) ?? null;
    }

    private mapLegalFormToOption(legalForm: ILegalFormDTO): INameId | null {
        if (!legalForm?._id) {
            return null;
        }

        return (
            this.legalFormsOptions.find((option) => option.id === legalForm._id) ?? {
                id: legalForm._id,
                name: this.getLocalizedLegalFormName(legalForm),
            }
        );
    }

    private getLocalizedLegalFormName(legalForm: ILegalFormDTO): string {
        const currentLanguage = String(this.currentLanguage).toLowerCase();
        const defaultLanguage = String(this.defaultLanguage).toLowerCase();

        return legalForm.text?.[currentLanguage] ?? legalForm.text?.[defaultLanguage] ?? legalForm.name;
    }

    // ── Existing handlers ─────────────────────────────────────────────────────

    public onCountrySelectionChange(selectionChange: MatSelectChange): void {
        if (selectionChange.value == null) {
            this.countrySelectionChange.emit(undefined);
            return;
        }
        this.countrySelectionChange.emit(selectionChange.value.id);
    }

    private legalFormChangeHandler(): void {
        const selectedLegalForm = this.companyFormGroup?.get('legalform').value;
        if (selectedLegalForm != null) {
            this.latestSelectedLegalForm = selectedLegalForm;
        }

        if (this.latestSelectedLegalForm == null) {
            this.companyFormGroup?.get('legalform').setValue(null);
            return;
        }

        const indexOfSelectedLegalForm = this.legalFormsOptions.findIndex((legalForm) => legalForm.id === this.latestSelectedLegalForm.id);

        const notFoundIndex = -1;
        if (indexOfSelectedLegalForm === notFoundIndex) {
            this.companyFormGroup?.get('legalform').setValue(null);
            this.showWarningLegalFormNotExist = true;
        } else {
            this.companyFormGroup?.get('legalform').setValue(this.latestSelectedLegalForm);
            this.showWarningLegalFormNotExist = false;
        }
    }

    public onCancelUpload() {
        this.showUploadDialog = false;
        this.logo = this.originalLogo;
        this.readFile(this.logo);
    }

    public readFile(file: File): void {
        if (!file) {
            this.uploadedLogo = null;
            return;
        }
        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            this.uploadedLogo = e.target?.result;
        };
        reader.readAsDataURL(file);
    }

    public newFileUploaded(file: File): void {
        if (file === null) {
            this.fileMetadata = null;
        }
        this.logo = file;
    }

    public onFileUploaded() {
        if (!this.fileSizeError) {
            this.showUploadDialog = false;
            this.readFile(this.logo);
            this.fileUploaded.emit(this.logo);
        }
        if (this.logo != null) {
            this.fileMetadata = {
                name: this.logo.name,
                size: this.logo.size,
            } as FileMetaData;
        }
    }
    /** REV-22155: the selected tags live in the form group, so they are saved with the company. */
    get selectedTagIds(): string[] {
        return (this.companyFormGroup?.get('tags')?.value as string[]) ?? [];
    }

    onTagSelectionChange(tagIds: string[]): void {
        this.companyFormGroup?.get('tags')?.setValue(tagIds);
        this.companyFormGroup?.get('tags')?.markAsDirty();
    }

    /** Role key → translated text, same keys as the person form. */
    personRoleLabel(role: string): string {
        return role ? this.i18next.t(`aumico_persons_form_role_${role}`).toString() : '';
    }

    personInitials(name: string): string {
        const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '';
        return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0].substring(0, 2).toUpperCase();
    }

}
